import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Pagination } from "../components/Pagination";
import { SearchInput } from "../components/SearchInput";
import {
  Alert,
  Card,
  EmptyState,
  TableSkeleton,
  PageHeader,
  PlatformRoleBadge,
  SuspendedBadge,
  Table,
  Td,
  Th,
} from "../components/ui";
import { formatDate, formatRelative } from "../lib/format";
import { useUsers } from "../lib/queries";

const LIMIT = 25;

export function UsersPage() {
  // Seeded from `?q=` so another page can land here already searching — the
  // email-domains page links a blocked domain to the accounts on it.
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [offset, setOffset] = useState(0);
  const { data, isPending, error } = useUsers({ q, limit: LIMIT, offset });

  function search(next: string) {
    setQ(next);
    setOffset(0);
  }

  return (
    <>
      <PageHeader title="Users" description="Every account on the platform, customers and staff." />

      <Card>
        <div className="border-b border-line px-5 py-3">
          <SearchInput value={q} onChange={search} placeholder="Search by name or email…" />
        </div>

        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            title={q ? "No matching users" : "No users yet"}
            description={q ? "Try a different search." : undefined}
          />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>User</Th>
                  <Th>Role</Th>
                  <Th numeric priority="sm">Orgs</Th>
                  <Th priority="md">Last login</Th>
                  <Th priority="lg">Joined</Th>
                </tr>
              }
            >
              {data.data.map((user) => (
                <tr key={user.id} className="hover:bg-raised">
                  <Td>
                    <Link
                      to={`/users/${user.id}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {user.name}
                    </Link>
                    <p className="text-xs text-fg-subtle">{user.email}</p>
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1.5">
                      {user.platformRole && <PlatformRoleBadge role={user.platformRole} />}
                      {user.suspendedAt && <SuspendedBadge />}
                      {!user.platformRole && !user.suspendedAt && (
                        <span className="text-fg-subtle">Customer</span>
                      )}
                    </div>
                  </Td>
                  <Td numeric priority="sm">{user.counts?.orgs ?? 0}</Td>
                  <Td className="text-fg-subtle" priority="md">
                    {formatRelative(user.lastLoginAt)}
                  </Td>
                  <Td className="text-fg-subtle" priority="lg">{formatDate(user.createdAt)}</Td>
                </tr>
              ))}
            </Table>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </>
  );
}
